import React, { FC, useEffect, useState, useCallback } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
} from "@material-ui/core";
import { ExpandMore as ExpandMoreIcon } from "@material-ui/icons";
import { SettingsLog } from "../../../../models/settingsLog";
import { getSettingsLogsByEntityName } from "../../../../services/get.data.service";
import { Button } from "react-bootstrap";

type Props = {
  entityName: string;
};

const PAGE_SIZE = 20;

const SettingsLogViewer: FC<Props> = ({ entityName }) => {
  const [logs, setLogs] = useState<SettingsLog[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState<number>(0);

  const loadLogs = async (pageIndex = 0, append = false): Promise<void> => {
    if (!entityName) return;
    
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setError(null);
    }
    
    try {
      const response = await getSettingsLogsByEntityName(entityName, pageIndex, PAGE_SIZE);
      if (response.success) {
        const newLogs = response.data;
        const totalCount = response.dataCount;
        
        setTotalCount(totalCount);
        
        if (append) {
          setLogs((prevLogs) => {
            const combined = (prevLogs ?? []).concat(newLogs);
            setHasMore(combined.length < totalCount);
            return combined;
          });
        } else {
          setLogs(newLogs);
          setHasMore(newLogs.length < totalCount);
        }

        setCurrentPage(pageIndex);
      } else {
        setError(response.message || "Failed to load settings logs");
      }
    } catch (err) {
      setError("Error loading settings logs");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore && logs) {
      loadLogs(currentPage + 1, true);
    }
  }, [loadingMore, hasMore, currentPage, logs]);

  useEffect(() => {
    setLogs(null);
    setCurrentPage(0);
    setHasMore(true);
    setTotalCount(0);
    loadLogs(0, false);
  }, [entityName]);

  const formatTimestamp = (timestamp: Date): string => {
    return new Date(timestamp).toLocaleString();
  };

  const formatSerializedData = (data: string | null | undefined): string => {
    if (!data) return "No data";
    
    try {
      const json = JSON.stringify(JSON.parse(data), null, 2)
      return json.substring(2, json.length - 2); // Remove outer braces for cleaner display
    } catch {
      return data;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <Box p={2}>
        <Typography color="textSecondary">
          No settings changes found for {entityName}
        </Typography>
      </Box>
    );
  }

  return (
    <Box role="table" aria-label="history">
      <Typography variant="h6" gutterBottom>
        Settings History ({totalCount > 0 ? `${logs?.length || 0} of ${totalCount}` : logs?.length || 0} entries)
      </Typography>
      
      {logs?.map((log) => (
        <Accordion role="row" key={log.settingsLogId} style={{ marginBottom: 8 }} aria-label="expand" >
          <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
            <Box display="flex" alignItems="center" width="100%">
              <Box flexGrow={1}>
                <Typography variant="body1">
                  {`${log.entityId} ${log.action.toLocaleLowerCase()} by `}<strong>{log.userEmail}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {formatTimestamp(log.timestamp)}
                </Typography>
              </Box>
            </Box>
          </AccordionSummary>
          
          {log.serializedData && (
            <AccordionDetails>
              <Box width="100%">                
                <Typography variant="subtitle2" gutterBottom>
                  Changes:
                </Typography>
                <Card variant="outlined">
                  <CardContent>
                    <pre className="settings-log-data">
                      {formatSerializedData(log.serializedData)}
                    </pre>
                  </CardContent>
                </Card>
              </Box>
            </AccordionDetails>
          )}
        </Accordion>
      ))}

      {hasMore && logs && logs.length > 0 && (
        <Box p={2} textAlign="center">
          <Button
            variant="link"
            size="sm"
            onClick={() => loadMore()}
            disabled={loadingMore || !hasMore}
          >
            Load More...
          </Button>
        </Box>
      )}

      {hasMore && logs && logs.length > 0 && (
        <Box
          display="flex"
          justifyContent="center"
          p={2}
          style={{ minHeight: '50px' }}
        >
          {loadingMore && <CircularProgress size={24} />}
        </Box>
      )}

      {/* Show message when all logs are loaded */}
      {!hasMore && logs && logs.length > 0 && (
        <Box p={2} textAlign="center">
          <Typography variant="body2" color="textSecondary">
            All settings changes loaded
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SettingsLogViewer;